import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useManageTrainingRoute from '../../../hooks/useManageTrainingRoute';
import { Color, Grid, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import EmptyState from '../../../components/EmptyState';
import ErrorMessage from '../../../components/ErrorMessage';
import { FormFieldContainer, TextFilterInput } from '../../../components/FormElements';
import { LoadingVideoPlaceholder, VideoPlaceholder } from '../../../components/Layout';
import LoadingState from '../../../components/LoadingState';
import VideoPlayer from '../../../containers/VideoPlayer';
import { useUser } from '../../authentication';
import { getTrainingContent } from '../api/getTrainingContent';
import { Content } from '../models/content';
import { ContentUploadStep } from './ContentUploadStep';
import { Form } from 'formik';
import { useStepConfig } from '../hooks/useStepConfig';
import { StepType } from '../models/stepType';
import { ActionButton } from './ActionButton';
import fileService from '../../../services/fileService';
import catalogService from '../../../services/catalogService';

const StyledFormFieldContainer = styled(FormFieldContainer)`
  margin-bottom: ${Grid._2};
`;

const ContentList = styled.div`
  display: grid;
  grid-gap: ${Grid._4};
  width: 100%;
  grid-gap: 0;
  grid-template-columns: 1fr;
  margin-top: 0;
  max-height: 400px;
  overflow-y: auto;
`;

const ContentItem = styled.div<{ selected?: boolean }>`
  border: 1px solid ${Color.Blue._15};
  color: ${Type.Color.medium};
  align-items: center;
  padding: ${Grid._4};
  cursor: pointer;
  background-color: ${props => (props.selected ? Color.Gray._10 : Color.white)};
  display: grid;
  grid-template-columns: 30px 40px 1fr auto;
  grid-column-gap: ${Grid._2};
  border-radius: 0;
  border-top: 0px;
  border-left: 0px;
  border-right: 0px;
  align-items: center;
`;

const CheckboxIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Color.Primary._50};
  font-size: ${Type.Scale._3};
  padding-left: ${Grid._3};
`;

const ContentIcon = styled.span`
  font-size: ${Type.Scale._3};
  width: auto;
  text-align: left;
  padding-bottom: 0;
  padding-left: ${Grid._3};
`;

const ContentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._1};
  text-align: left;
  padding: 0;
  width: auto;
`;

const ContentTitle = styled.span`
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
`;

const PreviewViewContainer = styled.div`
  margin-bottom: ${Grid._4};
`;

const SearchContainer = styled(Form)`
  display: flex;
  flex-wrap: wrap;
  gap: ${Grid._4};
  margin-bottom: ${Grid._4};

  > div {
    flex: 1 1 520px;
  }

  > button {
    flex: 1;
    min-width: 220px;
  }
`;

type ContentStepProps = {
  selectedContents: Content[];
  contentSearchQuery: string;
  setSelectedContents: (contents: Content[]) => void;
  setContentSearchQuery: (query: string) => void;
  stepType: StepType;
  currentStep: 'select' | 'form' | 'preview' | 'upload';
  allowMultipleSelection?: boolean;
  setCurrentStep: React.Dispatch<React.SetStateAction<'select' | 'form' | 'preview' | 'upload'>>;
  previewContent?: { name: string; id: string };
  setPreviewContent: React.Dispatch<React.SetStateAction<{ name: string; id: string } | undefined>>;
  onUploadComplete: (content: Content) => void;
};

export const ContentStep: React.FC<ContentStepProps> = ({
  selectedContents,
  contentSearchQuery,
  setSelectedContents,
  setContentSearchQuery,
  stepType,
  currentStep,
  allowMultipleSelection = true,
  setCurrentStep,
  previewContent,
  setPreviewContent,
  onUploadComplete,
}) => {
  const { user } = useUser();
  const { manageTrainingType } = useManageTrainingRoute();
  const isAuthoring = manageTrainingType === 'author';

  const [contents, setContents] = useState<{ loading: boolean; data?: Content[]; error?: boolean }>({
    loading: false,
  });
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);

  const handlePreviewContent = (content: { name: string; id: string }) => {
    setPreviewContent(content);
    setCurrentStep('preview');
  };

  const StepConfig = useStepConfig({ setCurrentStep });

  useEffect(() => {
    if (isAuthoring) {
      setContents({ loading: true });
      catalogService
        .getCatalogIdForSite()
        .then((catalogOwnerId: string) => {
          setOwnerId(catalogOwnerId);
          return getTrainingContent(catalogOwnerId, stepType).then(response => {
            setContents({ loading: false, data: response });
          });
        })
        .catch(() => {
          setContents({ loading: false, error: true });
        });
    } else if (user?.lastSelectedAccount) {
      setOwnerId(user.lastSelectedAccount);
      setContents({ loading: true });
      getTrainingContent(user.lastSelectedAccount, stepType)
        .then(response => {
          setContents({ loading: false, data: response });
        })
        .catch(() => {
          setContents({ loading: false, error: true });
        });
    }
  }, [user?.lastSelectedAccount, stepType]);

  const filteredContents =
    contents?.data?.filter(content => content.name.toLowerCase().includes(contentSearchQuery.toLowerCase())) || [];

  const handleItemClick = (content: Content) => {
    if (!allowMultipleSelection) {
      setSelectedContents([content]);
      return;
    }

    const isSelected = selectedContents.some(c => c.id === content.id);
    const newSelectedContents = isSelected
      ? selectedContents.filter(c => c.id !== content.id)
      : [...selectedContents, content];

    setSelectedContents(newSelectedContents);
  };

  if (currentStep === 'upload') {
    return (
      <ContentUploadStep
        stepType={stepType}
        selectedContents={selectedContents}
        setSelectedContents={setSelectedContents}
        setContents={setContents}
        user={user}
        ownerId={ownerId}
        onUploadComplete={onUploadComplete}
      />
    );
  }

  if (previewContent) {
    return (
      <StyledFormFieldContainer>
        <PreviewViewContainer>
          <h3 style={{ marginBottom: Grid._4 }}>{previewContent.name}</h3>
          <VideoPlaceholder>
            {isPreviewLoading && <LoadingVideoPlaceholder />}
            <VideoPlayer
              setIsLoading={setIsPreviewLoading}
              videoId={previewContent.id}
              onEnd={() => null}
              defaultAudioOnly={stepType === 'audio'}
            />
          </VideoPlaceholder>
        </PreviewViewContainer>
      </StyledFormFieldContainer>
    );
  }

  return (
    <>
      <SearchContainer>
        <TextFilterInput
          id="contentSearchFilter"
          value={contentSearchQuery || ''}
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) => setContentSearchQuery(e.target.value)}
          clearInput={() => {
            setContentSearchQuery('');
          }}
          placeholder={`Search ${stepType === 'audio' ? 'audio' : stepType}s`}
        />
        <PrimaryButton
          onClick={() => {
            StepConfig[stepType as keyof typeof StepConfig].primaryAction();
          }}
        >
          {StepConfig[stepType as keyof typeof StepConfig].primaryActionText}
        </PrimaryButton>
      </SearchContainer>
      <ContentList>
        {contents.loading ? (
          <LoadingState />
        ) : contents.error ? (
          <ErrorMessage />
        ) : filteredContents.length > 0 ? (
          filteredContents.map(content => {
            const isSelected = selectedContents.some(c => c.id === content.id);
            return (
              <ContentItem key={content.id} selected={isSelected} onClick={() => handleItemClick(content)}>
                <CheckboxIcon>
                  {isSelected && <i className="fas fa-check-circle" aria-label="Selected"></i>}
                  {!isSelected && (
                    <i className="far fa-circle" style={{ color: Color.Gray._30 }} aria-label="Not Selected"></i>
                  )}
                </CheckboxIcon>
                <ContentIcon>
                  <i className={StepConfig[stepType as keyof typeof StepConfig].icon}></i>
                </ContentIcon>
                <ContentDetails>
                  <ContentTitle>{content.name}</ContentTitle>
                </ContentDetails>

                {content.type === 'Download' && (
                  <ActionButton
                    onClick={() => window.open(fileService.urlOpenFile(content.id, content.name))}
                    aria-label="Download File"
                  >
                    <i className="fas fa-download" />
                    Download
                  </ActionButton>
                )}

                {content.type === 'Video' && (
                  <ActionButton onClick={() => handlePreviewContent({ name: content.name, id: content.id })}>
                    <i className="fas fa-play" />
                    Preview
                  </ActionButton>
                )}
              </ContentItem>
            );
          })
        ) : (
          <EmptyState title="No content found" description="Try searching for something else or create some content." />
        )}
      </ContentList>

      <StyledFormFieldContainer>
        <input type="hidden" name="videoId" value={selectedContents.map(c => c.id).join(',')} />
      </StyledFormFieldContainer>
    </>
  );
};
